import { Component, OnDestroy, OnInit } from '@angular/core'
import { DOCUMENT } from '@angular/common';
import {
  PoMenuItem,
  PoThemeService,
  PoTagType,
  PoToolbarAction,
  PoToolbarProfile
} from '@po-ui/ng-components'
import { AuthService } from 'src/app/services/auth.service'
import { Subject, forkJoin, of } from 'rxjs'
import { catchError, takeUntil } from 'rxjs/operators'
import { environment } from '../environments/environment';
import { NavigationEnd, Router } from '@angular/router';
import { CartStorageService } from './services/cart-storage.service';
import { UserProfileService } from './services/user-profile.service';
import packageInfo from '../../package.json';
import { filter } from 'rxjs/operators';
import { Meta } from '@angular/platform-browser';
import { Inject } from '@angular/core';

interface BottomNavigationItem {
  id: string;
  icon: string;
  label: string;
  link: string;
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly version = packageInfo.version;
  readonly isDevelopment = !environment.production;
  readonly environmentLabel = environment.production ? 'Produção' : 'Desenvolvimento';
  readonly environmentType = environment.production
    ? PoTagType.Success
    : PoTagType.Warning;
  readonly versionType = PoTagType.Neutral;
  readonly toolbarTitle = environment.production ? 'Flink' : 'Flink DEV';

  logo = ''
  quantidadeCarrinho = 0
  menuCollapsed = false
  loggedIn = false

  profile: PoToolbarProfile = {
    avatar: '',
    subtitle: '',
    title: this._auth.getFirstName() || this._auth.getTokenSubject() || 'Usuário'
  }

  profileActions: Array<PoToolbarAction> = [
    { icon: 'ICON_USER', label: 'Meu perfil', url: '/profile' },
    { icon: 'ICON_EXIT', label: 'Sair', type: 'danger', separator: true, url: '/logout' }
  ]

  toolbarActions: Array<PoToolbarAction> = []
  menus: Array<PoMenuItem> = []
  bottomNavigationItems: Array<BottomNavigationItem> = [
    {
      id: 'inicio',
      icon: 'an an-house',
      label: 'Início',
      link: '/'
    },
    {
      id: 'documentos-aprovacao',
      icon: 'an an-file-text',
      label: 'Docs',
      link: '/documentos/aprovacao'
    },
    {
      id: 'compras-catalogo',
      icon: 'an an-shopping-bag',
      label: 'Catálogo',
      link: '/compras/catalogo'
    },
    {
      id: 'compras-carrinho',
      icon: 'ICON_CART',
      label: 'Carrinho',
      link: '/compras/carrinho'
    },
    {
      id: 'compras-pedidos',
      icon: 'an an-list-checks',
      label: 'Pedidos',
      link: '/compras/pedidos'
    }
  ]
  private cartMenuItem?: PoMenuItem
  private currentPath = '/'

  constructor(
    private _auth: AuthService,
    private cartStorageService: CartStorageService,
    private router: Router,
    private userProfileService: UserProfileService,
    private themeService: PoThemeService,
    private metaService: Meta,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit(): void {
    this.themeService.setDensityMode('small');
    this.configureDocumentTitle();
    this.configureBrand();
    this.configureNavigation();
    this.updateCartNavigation(0);
    this.loggedIn = this.isLogged();

    this.cartStorageService.quantity$
      .pipe(takeUntil(this.destroy$))
      .subscribe(quantity => this.updateCartNavigation(quantity));

    this.updateMenuState(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        this.loggedIn = this.isLogged();
        this.updateMenuState(event.urlAfterRedirects);
      });

    if (this.loggedIn) {
      this.loadProfile();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  refreshShell(): void {
    if (this.isLogged()) {
      this.loggedIn = true;
      this.loadProfile(true);
    }
  }

  isLogged(): boolean {
    return this._auth.hasValidAccessToken()
  }

  onMenuToggle(expanded: boolean): void {
    this.menuCollapsed = !expanded;
  }

  isBottomNavActive(link: string): boolean {
    if (link === '/') {
      return this.currentPath === '/';
    }

    return this.currentPath === link || this.currentPath.startsWith(`${link}/`);
  }

  private configureBrand(): void {
    this.logo = this.isDevelopment
      ? 'assets/flink/logo/flink-wordmark-dev.svg'
      : 'assets/flink/logo/flink-wordmark.svg';
  }

  private configureDocumentTitle(): void {
    const appTitle = this.isDevelopment ? 'Flink DEV' : 'Flink';

    this.metaService.updateTag({
      name: 'apple-mobile-web-app-title',
      content: appTitle
    });
    this.metaService.updateTag({
      name: 'theme-color',
      content: this.isDevelopment ? '#8a5a00' : '#5f6f52'
    });
    this.configureManifest();
  }

  private configureManifest(): void {
    const manifest = this.document.querySelector<HTMLLinkElement>('link[rel="manifest"]');

    if (manifest) {
      manifest.href = this.isDevelopment
        ? 'manifest.dev.webmanifest'
        : 'manifest.webmanifest';
    }
  }

  private updateMenuState(url: string): void {
    const path = url.split('?')[0].split('#')[0];
    this.currentPath = path || '/';
    this.menuCollapsed = path !== '/';
  }

  private updateCartNavigation(quantity: number): void {
    this.quantidadeCarrinho = quantity;

    this.toolbarActions = [{
      icon: 'ICON_CART',
      label: 'Abrir carrinho',
      action: () => void this.router.navigate(['/compras/carrinho'])
    }];

    if (this.cartMenuItem) {
      this.cartMenuItem.badge = quantity > 0
        ? { value: quantity, color: 'color-07' }
        : undefined;
    }
  }

  private configureNavigation(): void {
    const cartMenuItem: PoMenuItem = {
      id: 'compras-carrinho',
      label: 'Carrinho',
      shortLabel: 'Carrinho',
      link: '/compras/carrinho'
    };

    this.cartMenuItem = cartMenuItem;
    this.menus = [
      {
        id: 'inicio',
        label: 'Início',
        shortLabel: 'Início',
        link: '/',
        icon: 'an an-house'
      },
      {
        id: 'documentos',
        label: 'Documentos',
        shortLabel: 'Documentos',
        icon: 'an an-file-text',
        subItems: [{
          id: 'documentos-aprovacao',
          label: 'Aprovação de documentos',
          shortLabel: 'Aprovação',
          link: '/documentos/aprovacao'
        }]
      },
      {
        id: 'compras',
        label: 'Compras',
        shortLabel: 'Compras',
        icon: 'ICON_CART',
        subItems: [
          {
            id: 'compras-catalogo',
            label: 'Catálogo',
            shortLabel: 'Catálogo',
            link: '/compras/catalogo'
          },
          cartMenuItem,
          {
            id: 'compras-pedidos',
            label: 'Pedidos',
            shortLabel: 'Pedidos',
            link: '/compras/pedidos'
          }
        ]
      }
    ];
  }

  private loadProfile(force = false): void {
    forkJoin({
      summary: this.userProfileService.loadSummary(force),
      avatar: this.userProfileService.loadAvatar(force).pipe(
        catchError(() => of(''))
      )
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: ({ summary, avatar }) => {
        this.profile = {
          avatar,
          title: summary.name || this.profile.title,
          subtitle: summary.roleDescription
        }
      }
    });
  }
}
